import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QuantityInput } from '../../../../shared/ui/quantity-input';
import { NumberFormatter } from '../../../../shared/ui/number-formatter';
import { Item } from '../../../../types/interfaces';
import { ImageWithFallback } from '../../../../shared/ui/image-with-fallback';
import { Icon } from '../../../../shared/assets/Icon';
import { useAppSelector } from '../../../../app/store/store';
import { getCurrencySymbol } from '@shared/lib/currency';

interface CartItemUIProps {
  item: Item;
  quantity: number;
  verificationQuantity: number;
  passportQuantity: number;
  onQuantityChange: (value: number) => void;
  onVerificationQuantityChange: (value: number) => void;
  onPassportQuantityChange: (value: number) => void;
  removeFromCart: (id: number) => void;
  removeVerifyHandler: (id: number) => void;
  removePassportHandler: (id: number) => void;
  addingItemToCart: ({
    quantity,
    verificationQuantity,
    passportQuantity,
  }: {
    quantity: number;
    verificationQuantity: number;
    passportQuantity: number;
  }) => void;
  isSelected?: boolean;
  onSelectChange?: (checked: boolean) => void;
}

export const CartItemUI = ({
  item,
  quantity,
  verificationQuantity,
  passportQuantity,
  onQuantityChange,
  onVerificationQuantityChange,
  onPassportQuantityChange,
  removeFromCart,
  removeVerifyHandler,
  removePassportHandler,
  addingItemToCart,
  isSelected = false,
  onSelectChange,
}: CartItemUIProps): JSX.Element => {
  const currency = useAppSelector((state) => state.cart.currency);
  const currencySymbol = getCurrencySymbol(currency);
  const navigate = useNavigate();
  // Переход в карточку товара. Тонкий момент: /catalog/:id ждёт id
  // КАРТОЧКИ (Card на бэке), а не id Item'а (конкретного варианта
  // исполнения, который лежит в корзине). Раньше тут стоял
  // `item.id` — для большинства SKU это давало 404, потому что
  // Item.id != Card.id. Сейчас бэк отдаёт `item.card_id` через
  // ItemSerializer.get_card_id; используем его. Если по какой-то
  // причине card_id null (Item.card может быть null в БД), не
  // делаем элементы кликабельными — лучше «пассивная» строка,
  // чем 404.
  const cardId = item.card_id ?? null;
  const goToProduct = cardId
    ? () => navigate(`/catalog/${cardId}`)
    : undefined;
  const isClickable = Boolean(goToProduct);
  return (
    <div className="relative flex gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-200 pt-10 pr-10" data-qa="cart_item">
      {onSelectChange && (
        <label className="absolute top-3 left-3 cursor-pointer">
          <input
            type="checkbox"
            className="sr-only"
            checked={isSelected}
            onChange={(e) => onSelectChange(e.target.checked)}
          />
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? 'bg-[#e3282a] border-[#e3282a]'
                : 'bg-white border-gray-300 hover:border-[#e3282a]'
            }`}
          >
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </label>
      )}
      {/* Фото — кликабельное, если есть card_id. Кнопка-обёртка,
          а не <a>, чтобы react-router сделал client-side navigation
          без перезагрузки. aria-label дублирует имя товара для
          screen reader'ов. Если карточки нет (исторический Item
          без FK на Card) — рендерим как обычный <div>: не водить
          пользователя в 404. */}
      {isClickable ? (
        <button
          type="button"
          onClick={goToProduct}
          aria-label={`Открыть карточку товара «${item.name}»`}
          className="flex-shrink-0 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#e3282a] rounded-xl"
        >
          <ImageWithFallback
            src={item.photo || '/assets/images/no_image.png'}
            alt={item.name}
            className="w-20 h-20 object-cover rounded-xl group-hover:opacity-80 transition-opacity"
          />
        </button>
      ) : (
        <ImageWithFallback
          src={item.photo || '/assets/images/no_image.png'}
          alt={item.name}
          className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
        />
      )}
      <div className="flex-1 space-y-2">
        <div className="flex items-start mt-5 justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Раньше тут стоял `line-clamp-2` — длинные названия
                датчиков обрезались до 2 строк с многоточием, и юзер
                не видел полного наименования. Сейчас полная строка,
                с `break-words` чтобы очень длинные слова/коды не
                распирали ширину карточки. Если у Item'а нет card_id
                (см. выше), показываем имя обычным <p> без перехода —
                иначе клик ведёт в 404. */}
            {isClickable ? (
              <button
                type="button"
                onClick={goToProduct}
                className="text-left w-full text-sm font-medium text-gray-900 hover:text-[#e3282a] transition-colors break-words leading-snug focus:outline-none focus-visible:underline"
                title={item.name}
              >
                {item.name}
              </button>
            ) : (
              <p
                className="text-sm font-medium text-gray-900 break-words leading-snug"
                title={item.name}
              >
                {item.name}
              </p>
            )}
            {/* Цена за 1 шт. — отдельная подпись под названием.
                Показываем только когда есть рассчитанная цена
                (item.price выставляется calculateCartPrices'ом —
                для гарантийных позиций без прайса там 0). */}
            {!!item.price && (
              <p className="text-xs text-gray-500 mt-1">
                Цена за 1 шт:{' '}
                <span className="text-gray-700 font-medium">
                  <NumberFormatter
                    number={item.price}
                    postfix={` ${currencySymbol}`}
                  />
                </span>
              </p>
            )}
          </div>

          <div className="flex-shrink-0">
            <QuantityChangeHandler value={quantity} onChange={onQuantityChange} onRemove={() => removeFromCart(item.id)} />
          </div>

          <div className="flex-shrink-0 min-w-[100px] text-right">
            <NumberFormatter
              className='text-base font-semibold text-gray-900 font-[18px]'
              number={item?.totalPrice ? +item.totalPrice : 0}
              postfix={` ${currencySymbol}`}
            />
          </div>
        </div>
        {!!item.passport_item && (
          <div className="flex items-center mt-5 justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600">Паспорт</p>
            </div>
            <div className="flex-shrink-0">
              <QuantityChangeHandler value={passportQuantity} onChange={onPassportQuantityChange} onRemove={() => removePassportHandler(item.id)} />
            </div>

            <div className="flex-shrink-0 min-w-[100px] text-right">
              <NumberFormatter
                className='text-base font-semibold text-gray-900 font-[18px]'
                number={item?.passportTotalPrice || 0}
                postfix={` ${currencySymbol}`}
              />
            </div>
          </div>
        )}

        {!!item.verification_item && (
          <div className="flex items-center mt-5 justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-600">Поверка</p>
            </div>

            <div className="flex-shrink-0">
              <QuantityChangeHandler value={verificationQuantity} onChange={onVerificationQuantityChange} onRemove={() => removeVerifyHandler(item.id)} />
            </div>

            <div className="flex-shrink-0 min-w-[100px] text-right">
              <NumberFormatter
                className='text-base font-semibold text-gray-900 font-[18px]'
                number={item?.verificationTotalPrice || 0}
                postfix={` ${currencySymbol}`}
              />
            </div>
          </div>
        )}
      </div>

      <div className="absolute top-3 right-0">
        <RemoveItemButton
          removeFromCart={removeFromCart}
          item={item}
        />
      </div>
    </div>
  );
};

interface RemoveItemButtonProps {
  removeFromCart: (id: number) => void;
  item: Item;
}

function RemoveItemButton({ removeFromCart, item }: RemoveItemButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowConfirm(true)}
        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
        aria-label="Удалить"
      >

        <Icon name="trash-line" />

      </button>

      {showConfirm && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowConfirm(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-sm font-medium text-gray-900">
                Удалить товар из корзины?
              </h4>
              <button
                onClick={() => setShowConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="trash-line" />
              </button>
            </div>
            <button
              onClick={() => {
                removeFromCart(item.id);
                setShowConfirm(false);
              }}
              className="w-full px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              Удалить
            </button>
          </div>
        </>
      )}
    </div>
  );
}



const QuantityChangeHandler = ({ value, onChange, onRemove }: { value: number, onChange: (value: number) => void, onRemove?: () => void }) => {
  const handleIncrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange(Math.min(value + 1, 10000));
  };

  const handleDecrement = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (value === 1 && onRemove) {
      onRemove();
    } else if (value > 0) {
      onChange(Math.max(value - 1, 0));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = parseInt(e.target.value, 10);
    if (isNaN(inputValue) || inputValue < 1) {
      if (onRemove) {
        onRemove();
      }
      return;
    }
    onChange(Math.max(Math.min(inputValue, 10000), 1));
  };

  // На скрине из бага видно «10C» вместо «100» — это и есть здесь:
  // у input была фиксированная ширина `w-10` (40px) с `text-center`,
  // в которой 3 символа уже не помещались, и последняя цифра
  // обрезалась прямоугольником поля. Сейчас:
  //   • ширина увеличена (`w-[60px]`) — с запасом под 5-значный
  //     максимум (Math.min с 10000);
  //   • `tabular-nums` — моноширинные цифры, ширина не дёргается при
  //     +/-;
  //   • inputMode/pattern — только цифры с клавиатуры (на мобильных
  //     откроется числовой keypad).
  return (
    <div className='flex items-center gap-2'>
      <button onClick={handleDecrement} className='w-[20px] h-[20px] flex items-center p-1 justify-center bg-gray-100 font-medium rounded hover:bg-gray-200 transition-colors'>
        <Icon name="minus" />
      </button>
      <input
        type='text'
        inputMode='numeric'
        pattern='[0-9]*'
        value={value === 0 ? '0' : value}
        onChange={handleInputChange}
        className='w-[60px] h-[24px] px-2 text-center rounded border border-gray-200 tabular-nums text-sm font-medium focus:outline-none focus:border-[#e3282a]'
      />
      <button onClick={handleIncrement} className='w-[20px] h-[20px] flex items-center p-1 justify-center bg-gray-100 font-medium rounded hover:bg-gray-200 transition-colors'>
        <Icon name="plus" />
      </button>
    </div>
  )
}